import { authService } from '@/services/auth-service';
import { navigate } from '@/utils/navigate';
import { applyDevWatermark } from '@/services/dev-watermark';

declare const google: {
  accounts: {
    id: {
      initialize(config: {
        client_id: string;
        callback: (response: { credential: string }) => void;
        auto_select?: boolean;
      }): void;
      renderButton(parent: Element, options: Record<string, unknown>): void;
    };
  };
};

function loadGsiScript(): Promise<void> {
  if (document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Sign-In'));
    document.head.appendChild(script);
  });
}

class NuLogin extends HTMLElement {
  protected readonly root: ShadowRoot;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.root.innerHTML = `
      <style>
        :host { display: block; }
        *, *::before, *::after { box-sizing: border-box; }

        .page {
          min-height: 100vh;
          background: var(--paper, #E4DED2);
          color: var(--ink, #2A2622);
          font-family: 'Inter', sans-serif;
          line-height: 1.55;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .top-bar {
          width: 100%;
          display: flex;
          align-items: center;
          padding: 14px 20px;
          border-bottom: 1px solid var(--line, #C9C0AC);
          background: rgba(228,222,210,0.92);
          backdrop-filter: blur(6px);
          position: sticky;
          top: 0;
          z-index: 10;
        }
        .brand {
          font-family: 'Space Grotesk', sans-serif;
          font-weight: 700;
          font-size: 18px;
          letter-spacing: -0.02em;
          display: flex;
          align-items: center;
          gap: 7px;
          text-decoration: none;
          color: inherit;
        }
        .dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          background: var(--kiln, #C97A2E);
          display: inline-block;
          flex-shrink: 0;
        }

        .content {
          width: 100%;
          max-width: 420px;
          padding: 48px 20px 32px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        }

        .ticket-card {
          width: 100%;
          background: var(--paper-light, #FAF7F0);
          border: 1px solid var(--line, #C9C0AC);
          border-radius: 6px;
          overflow: hidden;
        }
        .perf {
          height: 12px;
          background-image: radial-gradient(circle at 6px 6px, var(--paper, #E4DED2) 4px, var(--paper-light, #FAF7F0) 4.3px);
          background-size: 16px 12px;
          background-repeat: repeat-x;
        }
        .card-body {
          padding: 24px 28px 28px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
        }
        .eyebrow {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 11px;
          letter-spacing: .08em;
          text-transform: uppercase;
          color: var(--kiln-dark, #A15F1F);
          align-self: flex-start;
          margin: 0;
        }

        .signin-wrap {
          display: flex;
          justify-content: center;
          min-height: 44px;
          width: 100%;
        }

        .error-msg {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 12px;
          color: #b94a2c;
          text-align: center;
          margin: 0;
        }
      </style>

      <div class="page">
        <header class="top-bar">
          <a class="brand" href="/"><span class="dot"></span>notify-u</a>
        </header>

        <div class="content">
          <div class="ticket-card">
            <div class="perf"></div>
            <div class="card-body">
              <p class="eyebrow">Sign in to continue</p>
              <div class="signin-wrap" id="g-signin-btn"></div>
              <p class="error-msg" id="error-msg" hidden></p>
            </div>
            <div class="perf"></div>
          </div>
        </div>
      </div>
    `;

    applyDevWatermark(this.root);
    this._initSignIn();
  }

  private async _initSignIn() {
    try {
      const [config] = await Promise.all([
        fetch('/api/config').then(r => r.json()),
        loadGsiScript(),
      ]);

      google.accounts.id.initialize({
        client_id: config.googleClientId,
        callback: (response) => this._handleCredential(response.credential),
      });

      google.accounts.id.renderButton(
        this.root.querySelector('#g-signin-btn')!,
        { type: 'standard', theme: 'outline', size: 'large', width: 360 },
      );
    } catch (e) {
      this._showError('Could not load sign-in. Please refresh.');
    }
  }

  private async _handleCredential(credential: string) {
    try {
      await authService.loginWithGoogle(credential);
      const redirect = new URLSearchParams(window.location.search).get('redirect');
      navigate(redirect || '/app');
    } catch {
      this._showError('Sign-in failed. Please try again.');
    }
  }

  private _showError(msg: string) {
    const el = this.root.querySelector<HTMLParagraphElement>('#error-msg')!;
    el.textContent = msg;
    el.hidden = false;
  }
}

customElements.define('nu-login', NuLogin);